import { documentFetch } from "@/lib/document/docClient";
import { requireSupabase } from "@/lib/supabase";
import type { DocVariableValues } from "@/lib/document-builder/variables/variableFill";

export type DocExport = {
  id: string;
  organization_id: string;
  document_id: string;
  version_id: string;
  storage_bucket: string;
  storage_path: string;
  byte_size: number;
  variable_values: DocVariableValues;
  exported_by_user_id: string | null;
  exported_at: string;
};

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  export_id: string;
  document_name: string;
  version_number: number;
};

type CommitOk = {
  ok: true;
  export: DocExport;
};

type PreviewOk = {
  ok: true;
  document_id: string;
  version_id: string;
  document_name: string;
  version_number: number;
  variable_values: DocVariableValues;
};

export async function requestDocRenderPreview(body: {
  document_id: string;
  version_id: string;
  variable_values: DocVariableValues;
}) {
  const r = await documentFetch<PreviewOk | { ok: false; error: string }>(
    "/api/doc/render-preview",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!r.ok) return { ok: false as const, error: r.error };
  if (!r.data.ok) {
    return { ok: false as const, error: r.data.error ?? "Preview validation failed" };
  }
  return { ok: true as const, data: r.data };
}

export type UploadDocExportResult =
  | { ok: true; export: DocExport }
  | { ok: false; error: string };

export async function uploadDocExportPdf(args: {
  documentId: string;
  versionId: string;
  variableValues: DocVariableValues;
  blob: Blob;
}): Promise<UploadDocExportResult> {
  const sb = requireSupabase();
  const byteSize = args.blob.size;
  if (byteSize <= 0) {
    return { ok: false, error: "Generated PDF is empty — nothing to upload." };
  }

  const sign = await documentFetch<SignOk | { ok: false; error: string }>("/api/doc/export-sign", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      document_id: args.documentId,
      version_id: args.versionId,
      content_type: "application/pdf",
      byte_size: byteSize,
      variable_values: args.variableValues,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!sign.data.ok) {
    const err = (sign.data as { error?: string }).error;
    return { ok: false, error: err ?? "Sign response invalid" };
  }
  const s = sign.data;

  const { error: upErr } = await sb.storage
    .from(s.bucket)
    .uploadToSignedUrl(s.path, s.token, args.blob, {
      contentType: "application/pdf",
      upsert: false,
    });
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await documentFetch<CommitOk | { ok: false; error: string }>(
    "/api/doc/export-commit",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        document_id: args.documentId,
        version_id: args.versionId,
        export_id: s.export_id,
        object_path: s.path,
        byte_size: byteSize,
        variable_values: args.variableValues,
      }),
    },
  );
  if (!commit.ok) return { ok: false, error: commit.error };
  if (!commit.data.ok) {
    const err = (commit.data as { error?: string }).error;
    return { ok: false, error: err ?? "Commit response invalid" };
  }
  return { ok: true, export: commit.data.export };
}

export function listDocExports(args: {
  documentId: string;
  versionId?: string;
  limit?: number;
}) {
  const q = new URLSearchParams({ document_id: args.documentId });
  if (args.versionId) q.set("version_id", args.versionId);
  if (args.limit) q.set("limit", String(args.limit));
  return documentFetch<{ ok: true; exports: DocExport[] }>(
    `/api/doc/export-list?${q.toString()}`,
    { method: "GET" },
  );
}

export function downloadBlobAsFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
